import React, { useRef, useEffect, useState, useMemo } from "react";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import { useSelector, useDispatch } from "react-redux";
import ReusableBarChart from "./charts/BarChart";
import FooterNote from "../claimleaveview/FooterNote";
import moment from "moment";
import {
    getAllowedPoliciesAndReportingGroups,
    getSelectedIsfOrganizationIds,
     getPageReferrerTitle
} from "../../utils/helpers";
import ReusableDonutChart from "./charts/pieChart";
import { REPORTING_PERIOD, STATUS, DASHBOARD_MESSAGES } from "../../constants";
import Loader from "../../components/ui/Loader";
import RequestFailed from "../../components/alerts/RequestFailed";
import ApiFetch from "./ApiFetch";
import {
    generateCacheKey,
    saveToCacheDB,
    getFromCacheDB,
    clearAllExceptOrganization,
} from "../../utils/indexedDBCache";
import GTM from "../../GTM";
import { useParams } from "react-router-dom";

const DisabilitySnapshot = (props) => {
    const childRef = useRef(null);
    const dispatch = useDispatch();
    const [claimApiFetching, setClaimApiFetching] = useState(false);
    const [claimApiFulfilled, setClaimApiFulfilled] = useState("");
    const [openClaimsTableData, setOpenClaimsTableData] = useState([]);
    const [claimApiError, setClaimApiError] = useState(false);
    const [reportTableData, setReportTableData] = useState([]);
    const [disclaimer, setDisclaimer] = useState("");
    const [currentCacheKey, setCurrentCacheKey] = useState(null);
    const [isLoadingFromCache, setIsLoadingFromCache] = useState(false);
    const [shouldFetchApi, setShouldFetchApi] = useState(false);
    const previousOrgIdRef = useRef(null);

    const { orgid } = useParams();
    const { user } = useSelector((state) => state.auth);
    const { config } = useSelector((state) => state.app);

    // Selectors
    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
        portalUserData: storedPortalUserData,
        selectedDataViewType: dataViewType,
    } = useSelector((state) => state.reportOrganization);
    const { snapshotReportDetails } = useSelector(
        (state) => state.disabilitySnapshotData
    );
    const { totalParentOrganizations } = useSelector(
        (state) => state.organizations
    );

    // Memoized derived data
    const selectedReportDetails = useMemo(() => {
        if (!snapshotReportDetails?.length) return {};
        return (
            snapshotReportDetails.find(
                (obj) =>
                    obj.DataViewType === "ClaimSnapshot" &&
                    obj.SubViewName === "Max Duration"
            ) || {}
        );
    }, [snapshotReportDetails]);

    const reportParametersOptions =
        selectedReportDetails.ParametersOptions || [];
    const parameters = selectedReportDetails.Parameters || {};

    const getOptions = useMemo(() => {
        return (name) =>
            reportParametersOptions.find(
                (param) => param.parameterName === name
            )?.options || [];
    }, [reportParametersOptions]);

    const initializeFilterState = useMemo(() => {
        return (paramKey) =>
            parameters?.[paramKey]?.map((type) => ({
                name: type,
                value: type,
            })) || [];
    }, [parameters]);

    const reportingPeriodValue = useMemo(() => {
        return (
            getOptions(REPORTING_PERIOD).find(
                (option) => option.name === parameters?.reportingPeriod
            )?.value || []
        );
    }, [initializeFilterState, getOptions, parameters]);

    const filterStatusValues = useMemo(() => {
        return initializeFilterState(STATUS).flatMap(
            (selected) =>
                getOptions(STATUS).find(
                    (option) => option.name === selected.name
                )?.value || []
        );
    }, [initializeFilterState, getOptions]);

    const claimCoverageTypes = useMemo(() => {
        return initializeFilterState("CoverageType").map((obj) => obj.value);
    }, [initializeFilterState]);

    // Organization and policy logic
    const { selectedIsfOrganizationIds, relatedOrganizationNames } = useMemo(() => {
        return getSelectedIsfOrganizationIds(
            userSelectedOrganizationReports,
            reportId,
            selectedRelatedOrganizations,
            totalParentOrganizations
        );
    }, [
        userSelectedOrganizationReports,
        reportId,
        selectedRelatedOrganizations,
        totalParentOrganizations,
    ]);

    const { allowedPoliciesAndDivisions } = useMemo(() => {
        return getAllowedPoliciesAndReportingGroups(
            storedPortalUserData,
            selectedIsfOrganizationIds
        );
    }, [storedPortalUserData, selectedIsfOrganizationIds]);

    const initialPolicyDivision = useMemo(() => {
        return allowedPoliciesAndDivisions.flatMap((item) =>
            Array.isArray(item.divisions)
                ? item.divisions.map(
                      (division) => `${item.policyId}-${division.divisionId}`
                  )
                : []
        );
    }, [allowedPoliciesAndDivisions]);

    const getMaxOutClaimsLabel = useMemo(() => {
        if (!snapshotReportDetails?.length) return {};
        return (
            snapshotReportDetails.find(
                (obj) =>
                    obj.DataViewType === "ClaimSnapshot" &&
                    obj.SubViewName === "Max Duration"
            )?.SnapshotCustomLabels || {}
        );
    }, [snapshotReportDetails]);

    const snapshotViewIds = useMemo(() => {
        if (!snapshotReportDetails?.length) return [];
        return snapshotReportDetails
            .filter((obj) => obj.DataViewType === "ClaimSnapshot")
            .map((obj) => ({
                ViewId: obj.ViewId,
                Name: obj.Name,
                Description: obj.Description,
                SubViewName: obj.SubViewName
            }));
    }, [snapshotReportDetails]);

    // Render
    const organization =
        userSelectedOrganizationReports?.[reportId]?.organization;

    const organizationName =
        organization?.ORGANIZATIONGROUPNAME ||
        organization?.ORGNIZATIONNAME ||
        "";

    // Get organization ID for cache key
    const organizationId = organization?.ORGANIZATIONID || reportId;

    // Keep worker ref so it doesn't re-init every render
    const workerRef = useRef(null);

    useEffect(() => {
        workerRef.current = new Worker(
            new URL("../../workers/VizWorker.js", import.meta.url)
        );

        workerRef.current.onmessage = async (e) => {
            const { type, result, error } = e.data;
            if (type === "SUCCESS") {
                setOpenClaimsTableData(result.data);
                setClaimApiFetching(false);

                // Save to IndexedDB cache
                if (currentCacheKey && organizationId) {
                    await saveToCacheDB(
                        currentCacheKey,
                        {
                            chartData: result.data,
                            reportTableData,
                            disclaimer,
                        },
                        organizationId
                    );
                }
            } else if (type === "ERROR") {
                console.error("Worker error:", error);
                setClaimApiError(true);
                setClaimApiFetching(false);
            }
        };

        return () => {
            workerRef.current?.terminate();
        };
    }, [currentCacheKey, organizationId, reportTableData, disclaimer]);

    useEffect(() => {
        const checkInitialCache = async () => {
            if (!organizationId) {
                return;
            }
            // Use only organizationId for cache key since other values don't change
            const cacheKey = generateCacheKey(organizationId);

            // Check if organization has changed
            const orgChanged =
                previousOrgIdRef.current &&
                previousOrgIdRef.current !== organizationId;

            if (orgChanged) {
                // Clear all cache except for the current organization
                await clearAllExceptOrganization(organizationId);
                previousOrgIdRef.current = organizationId;
                setCurrentCacheKey(cacheKey);
                setShouldFetchApi(true);
                setOpenClaimsTableData([]); // Clear old data
                return;
            }

            setCurrentCacheKey(cacheKey);

            // Clear cache for other organizations on initial load
            if (!previousOrgIdRef.current) {
                await clearAllExceptOrganization(organizationId);
            }

            setIsLoadingFromCache(true);
            const cachedData = await getFromCacheDB(cacheKey);
            setIsLoadingFromCache(false);

            if (cachedData && cachedData.chartData) {
                setOpenClaimsTableData(cachedData.chartData);
                if (cachedData.reportTableData) {
                    setReportTableData(cachedData.reportTableData);
                }
                if (cachedData.disclaimer) {
                    setDisclaimer(cachedData.disclaimer);
                }
                setShouldFetchApi(false);
            } else {
                setShouldFetchApi(true);
            }

            previousOrgIdRef.current = organizationId;
        };

        checkInitialCache();
    }, [organizationId]);

    // Whenever reportTableData updates, send to worker
    useEffect(() => {
        if (!reportTableData || reportTableData.length === 0) {
            if (claimApiFulfilled === "fulfilled" && workerRef.current) {
                setClaimApiFetching(true);
                workerRef.current.postMessage({
                    type: "PROCESS_OPEN_CLAIMS",
                    payload: {
                        reportTableData: [],
                        getMaxOutClaimsLabel,
                        snapshotViewIds,
                    },
                });
            }
            return;
        }

        // Don't process if we already have cached data loaded
        if (openClaimsTableData && openClaimsTableData.length > 0) {
            return;
        }

        // Process new data from API
        if (workerRef.current) {
            setClaimApiFetching(true);
            workerRef.current.postMessage({
                type: "PROCESS_OPEN_CLAIMS",
                payload: {
                    reportTableData,
                    getMaxOutClaimsLabel,
                    snapshotViewIds,
                },
            });
        }
    }, [
        reportTableData,
        claimApiFulfilled,
        getMaxOutClaimsLabel,
        snapshotViewIds,
    ]);

    const removeHtmlTags = (content) => {
        const doc = new DOMParser().parseFromString(content, "text/html");
        return doc.body.textContent;
    };

    useEffect(() => {
        if (childRef.current) {
            const span1Content = childRef.current.span1?.innerHTML;
            const span2Content = childRef.current.span2?.innerHTML;
            const span2CleanContent = removeHtmlTags(span2Content);
            const mergingDisclaimerContent =
                span1Content +
                (span2CleanContent !== "undefined" && span2CleanContent !== null
                    ? span2CleanContent
                    : "");
            setDisclaimer(mergingDisclaimerContent);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

        let previousUrls = props.previousUrl;
        let page_referrer = previousUrls[previousUrls.length - 1];
        const gtm = new GTM(null);
       
    
        const page_referrer_url =
            page_referrer === "/login"
                ? `${config.REACT_APP_ISERVICES}:81/LanAuthentication`
                : `${config.REACT_APP_BASE_URL}${page_referrer}`;

        const page_referrer_title = getPageReferrerTitle(
            page_referrer_url,
            user,
            dispatch
        );
    
        useEffect(() => {
            const { userId, isUserInternal } = user;
            const userEmail = user.userEmail || "";
            const reportsEnabled= true
    
            gtm.PageView(
                "trends and benchmarking",
                organizationName,
                userId || "",
                isUserInternal ? "internal" : "external",
                window.location.href,
                `${config.REACT_APP_BASE_URL}${page_referrer}`,
                page_referrer_title,
                orgid,
                userEmail,
                relatedOrganizationNames,
                selectedIsfOrganizationIds,
                reportsEnabled
            );
        }, [20000]);

        useEffect(() => {
            if(claimApiError){
                gtm.Event(
                "Dashboard Comparisons",
                "field level error",
                "error",
                "Dashboard Comparisons",
                "dashboard_comparisons",
                "",
                "Dashboard Comparisons Error",
                DASHBOARD_MESSAGES.REQUEST_FAILED
            );
            }
        }, [claimApiError]);
        

    return (
        <>
            <Breadcrumb
                links={[
                    { path: "/dashboard", name: "Dashboards" },
                    {
                        path: "/dashboard/disability-snapshot",
                        name: dataViewType,
                    },
                ]}
            />

            <ApiFetch
                setOpenClaimsTableData={setOpenClaimsTableData}
                selectedIsfOrganizationIds={selectedIsfOrganizationIds}
                filterStatusValues={filterStatusValues}
                initialPolicyDivision={initialPolicyDivision}
                claimCoverageTypes={claimCoverageTypes}
                setClaimApiFetching={setClaimApiFetching}
                setClaimApiFulfilled={setClaimApiFulfilled}
                setClaimApiError={setClaimApiError}
                getMaxOutClaimsLabel={getMaxOutClaimsLabel}
                snapshotViewIds={snapshotViewIds}
                reportingPeriodValue={reportingPeriodValue}
                setReportTableData={setReportTableData}
                shouldFetchApi={shouldFetchApi}
                useFixedEffectiveDates
            />

            <div className="disability-snapshot-title">{organizationName}</div>
            {claimApiFetching || isLoadingFromCache ? (
                <Loader />
            ) : (
                <>
                    {!claimApiError && (
                        <div className="report-card-views">
                            {openClaimsTableData?.map((chart, index) => {
                                if (chart.variant === "bar" && chart.data) {
                                    return (
                                        <div
                                            key={chart.id || index}
                                            style={
                                                     { gridColumn: "1 / span 2" }
                                            }
                                        >
                                            <ReusableBarChart
                                                chartId={chart.id}
                                                title={chart.title}
                                                description={chart.description}
                                                data={chart.data}
                                                axisLabelOrientation={
                                                    chart.axisLabelOrientation
                                                }
                                                indexBy={chart.indexBy}
                                                variant={chart.variant}
                                                layout={chart.layout}
                                                chartValueOrientation={
                                                    chart.chartValueOrientation
                                                }
                                                chartValueType={chart.chartValueType}
                                                totalRecords={chart.totalRecords}
                                                disabilitySnapshot={true}
                                                errorMessage={
                                                    chart.error
                                                        ? chart.errorMessage
                                                        : undefined
                                                }
                                                viewId={chart.ViewId}
                                                // Pass data for direct navigation
                                                tableData={openClaimsTableData}
                                                reportDetails={snapshotReportDetails}
                                                disclaimer={disclaimer}
                                                // Pass cache-related props
                                                organizationId={organizationId}
                                                reportingPeriodValue={
                                                    reportingPeriodValue
                                                }
                                                claimCoverageTypes={claimCoverageTypes}
                                                filterStatusValues={filterStatusValues}
                                                cacheKey={currentCacheKey}
                                            />
                                        </div>
                                    );
                                } else if (chart.variant === "donut") {
                                    return (
                                        <ReusableDonutChart
                                            key={chart.id}
                                            title={chart.title}
                                            description={chart.description}
                                            data={chart.data}
                                            comparisons={chart.comparisons}
                                            totals={chart.totals}
                                            labels={chart.labels}
                                            totalRecords={chart.totals}
                                            disabilitySnapshot={true}
                                            errorMessage={
                                                chart.error
                                                    ? chart.errorMessage
                                                    : undefined
                                            }
                                            viewId={chart.ViewId}
                                            // Pass data for direct navigation
                                            tableData={openClaimsTableData}
                                            reportDetails={snapshotReportDetails}
                                            disclaimer={disclaimer}
                                            // Pass cache-related props
                                            organizationId={organizationId}
                                            reportingPeriodValue={reportingPeriodValue}
                                            claimCoverageTypes={claimCoverageTypes}
                                            filterStatusValues={filterStatusValues}
                                            cacheKey={currentCacheKey}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}

                    {claimApiError && (
                        <div className="mt-20p">
                            <RequestFailed />
                        </div>
                    )}

                    <FooterNote
                        ref={childRef}
                        date={moment().format("MM/DD/YYYY hh:mm A")}
                    />
                </>
            )}
        </>
    );
};

export default DisabilitySnapshot;
